import {Component, inject, Input, NgZone, OnDestroy, OnInit} from "@angular/core";
import {Router, RouterLink, RouterLinkActive} from "@angular/router";
import {firstValueFrom} from "rxjs";
import {AuthenticationService} from "../../services/services/authentication.service";
import {CustomizingService} from "../../services/services/customizing.service";
import {TranslateService, TranslatePipe} from "@ngx-translate/core";
import {TranslatableComponent} from "../../models/TranslatableComponent";
import {MatDialog} from "@angular/material/dialog";
// eslint-disable-next-line @typescript-eslint/naming-convention
import Config from "../../config/Config";
import {MaintenanceGuidelinesService} from "../../services/services/maintenanceGuidelines.service";
import {MaintenanceGuideline} from "../../models/MaintenanceGuideline";
import {AppEntity, AppEntityAccessType, Permission} from "../../models/AppPermissions";
import {FileDownloadService} from "../../services/services/file-download.service";
import {BugReportDialogComponent} from "../../../shared/dialogs/bug-report-dialog/bug-report-dialog.component";
import {AssignmentsNotificationDialogComponent} from "../../../shared/assignments-notification-dialog/assignments-notification-dialog.component";
import {UserNotificationService} from "../../services/services/userNotification.service";
import {AppearanceDialogComponent} from "src/app/shared/dialogs/user-settings/appearance-dialog.component";
import {AppearanceService} from "src/app/core/services/services/appearance.service";
import {ProfilePictureComponent} from "src/app/shared/generalized-list/profile-picture/profile-picture.component";
import {EmployeePermissionService} from "src/app/core/services/services/employeePermission.service";
import {SidenavControllerService} from "src/app/core/services/services/sidenav-controller.service";
import {DarkModeSetting} from "../../services/services/appearance-service.d/appearance-helper-dark-mode";
import {AnalyticsService} from "src/app/core/services/services/analytics/analytics.service";
import {Role} from "../../models/Role";
import {MatToolbar, MatToolbarRow} from "@angular/material/toolbar";
import {MatIcon} from "@angular/material/icon";
import {GlobalSearchBoxComponent} from "./global-search-box/global-search-box.component";
import {MatTooltip} from "@angular/material/tooltip";
import {MatBadge} from "@angular/material/badge";
import {MepPopoverDirective} from "src/app/shared/mep-popover/mep-popover.directive";
import {RequiresPermissionDirective} from "../../../shared/permission/directives/requires-permission.directive";
import {NewsNotificationComponent} from "../../../shared/news-notification/news-notification.component";
import {DatePipe} from "@angular/common";

@Component({
	selector: "app-navigation",
	templateUrl: "./navigation.component.html",
	styleUrls: ["./navigation.component.scss"],
	imports: [
		MatToolbar,
		MatToolbarRow,
		MatIcon,
		RouterLink,
		RouterLinkActive,
		GlobalSearchBoxComponent,
		MatTooltip,
		MatBadge,
		MepPopoverDirective,
		RequiresPermissionDirective,
		NewsNotificationComponent,
		DatePipe,
		TranslatePipe,
	],
})
export class NavigationComponent extends TranslatableComponent implements OnInit, OnDestroy {
	@Input() appImageFallback: string;

	systemDescription: string;
	environment: string;
	currentAuthenticatedStatus = true;
	displayGlobalSearchbar: boolean;
	userId: number;
	userName: string;
	deployedDate = new Date(document.lastModified);
	userRoles: string;
	releaseNotesLink = Config.releaseNotesLink;

	email: string;
	profilePicture: string;
	profilePictureFallback = "assets/mensch.svg";
	blinkInterval: ReturnType<typeof setInterval>;
	countNewNotifications: number;
	docChat = "assets/doc_chat.png";
	docChatLink = Config.docChatLink;

	userManualLink = Config.userManualLink;
	mepSharePointLink = Config.mepSharePointLink;
	guideline: MaintenanceGuideline;
	isEmployeeInDB = true;

	// remove, when news-notification no longer needed
	isLineManager: boolean;

	public appearanceService = inject(AppearanceService);
	public router = inject(Router);
	private readonly matDialog = inject(MatDialog);
	private readonly authService = inject(AuthenticationService);
	private readonly customizingService = inject(CustomizingService);
	private readonly maintenanceGuidelineService = inject(MaintenanceGuidelinesService);
	private readonly fileDownloadService = inject(FileDownloadService);
	private readonly userNotificationService = inject(UserNotificationService);
	private readonly ngZone = inject(NgZone);
	private readonly employeePermissionService = inject(EmployeePermissionService);
	protected sidenavControllerService = inject(SidenavControllerService);
	protected analyticsService: AnalyticsService = inject(AnalyticsService);
	private readonly _translate = inject(TranslateService);

	constructor() {
		super(inject(TranslateService), "NavigationComponent");
	}

	async ngOnInit() {
		this.getEnvironment();

		this.getUserRoles();

		// remove, when news-notification no longer needed
		await this.showNews().then((result) => {
			this.isLineManager = result;
		});

		await this.authService.getApplicationUser().then(async (user) => {
			if (user.employee) {
				this.userName = `${user.employee.firstName} ${user.employee.lastName}`;
				this.userId = user.employee.id;
				this.email = user.employee.email;
				this.profilePicture = Config.userPhoto + this.email;

				this.toggleBlinkClass(await this.checkIfNotificationExist());
				this.ngZone.runOutsideAngular(() => {
					this.blinkInterval = setInterval(
						async () => {
							this.ngZone.run(() => {
								this.checkIfNotificationExist().then((value) => this.toggleBlinkClass(value));
							});
						},
						60 * 60 * 1000,
					);
				});
			} else {
				this.userName = user.username;
				this.isEmployeeInDB = false;
			}
		});
		Promise.all([
			this.authService.hasPermission(Permission.ONLY_MY_BU, AppEntityAccessType.READ, AppEntity.EMPLOYEE),
			this.authService.hasPermission(Permission.ONLY_MY_BU, AppEntityAccessType.READ, AppEntity.PROJECT),
		]).then(([canReadEmployee, canReadProject]) => {
			this.displayGlobalSearchbar = canReadEmployee && canReadProject;
		});
		this.authService
			.hasPermission(Permission.TRUE, AppEntityAccessType.READ, AppEntity.MAINTENANCE_GUIDELINE)
			.then((data) => {
				if (data) {
					// For start
					this.maintenanceGuidelineService.getMaintenanceGuideline().subscribe((value) => {
						this.guideline = value;
					});

					// For changes
					this.maintenanceGuidelineService.share.subscribe((guideline) => {
						this.guideline = guideline;
					});
				}
			});
	}

	ngOnDestroy() {
		clearInterval(this.blinkInterval);
	}

	reportBug() {
		this.matDialog.open(BugReportDialogComponent, {
			disableClose: false,
			width: "50rem",
			panelClass: "mep-dialog",
			backdropClass: "light-backdrop",
		});
	}

	openColorSchemeSettings() {
		this.matDialog.open(AppearanceDialogComponent);
	}

	logout() {
		this.authService.logout();
	}

	async checkIfNotificationExist(): Promise<number> {
		if (this.userId) {
			return await firstValueFrom(this.userNotificationService.checkForNewNotification(this.userId)).catch((error) => {
				console.log("Failed to check for new notifications:", error);
				return 0;
			});
		} else {
			return 0;
		}
	}

	downloadGuideline(guideline: MaintenanceGuideline): void {
		this.fileDownloadService.downloadFile(guideline.downloadLink, guideline.name).subscribe();
	}

	getEnvironment() {
		const availableEnvironments = [
			"dev",
			"testGBP",
			"testMIA",
			"testMSA",
			"testM3",
			"intGBP",
			"prodGBP",
			"prodMSA",
			"prodM3",
			"prodMIA",
			"prodGILLARDON",
			"prodSCHWEIZ",
			"prodCONPLAN",
			"prodZT",
			"ci",
			"testGILLARDON",
			"testCOMPLIANCE",
			"testSCHWEIZ",
			"testCONPLAN",
			"testGLOBAL",
			"testGBA",
			"testSTAGING",
			"testQA"
		];
		this.customizingService.getEnvironment().then((env: string) => {
			this.environment = env;
			let key: string;
			if (availableEnvironments.includes(env)) {
				key = "mep.environment." + env;
			} else {
				key = "mep.environment.unknown";
			}
			this._translate.get(key).subscribe((name) => {
				this.systemDescription = name;
			});
		});
	}

	async openAssignmentsNotificationDialog() {
		this.matDialog
			.open(AssignmentsNotificationDialogComponent, {
				panelClass: "mep-dialog",
			})
			.afterClosed()
			.subscribe(() => {
				this.userNotificationService.clearAllFinishedInfos();
			});
		this.toggleBlinkClass(0);
		this.countNewNotifications = null;
	}

	toggleBlinkClass(countNewNotification: number): void {
		const bellIconLink = document.querySelector(".bell-icon-link");
		if (bellIconLink) {
			if (countNewNotification > 0) {
				this.countNewNotifications = countNewNotification;
				bellIconLink.classList.add("blink");
			} else {
				bellIconLink.classList.remove("blink");
			}
		}
	}

	private getUserRoles() {
		firstValueFrom(this.authService.getUserRoles()).then((result) => {
			this.userRoles = "";
			result.forEach((role) => {
				this.userRoles = this.userRoles.concat(this.translate.instant("mep.components.role-translated." + role) + ", ");
			});
			this.userRoles = this.userRoles.slice(0, this.userRoles.length - 2);
		});
	}

	// remove, when news-notification no longer needed
	showNews(): Promise<boolean> {
		const validRoles = new Set([
			Role.DEPARTMENT_MANAGER,
			Role.DIVISION_MANAGER,
			Role.BUSINESS_UNIT_MANAGER,
			Role.EXECUTIVE,
			Role.BUSINESS_UNIT_LEADER,
		]);

		return firstValueFrom(this.authService.getUserRoles()).then((roles) => {
			return roles.some((role) => validRoles.has(role));
		});
	}
	// eslint-disable-next-line @typescript-eslint/naming-convention
	protected readonly DarkModeSetting = DarkModeSetting;
	// eslint-disable-next-line @typescript-eslint/naming-convention
	protected readonly ProfilePictureComponent = ProfilePictureComponent;
}
